import { useEffect, useMemo, useRef, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ChatBubbleEmpty,
  Clock,
  Cube,
  CubeBandage,
  Edit,
  FastArrowDown,
  FastArrowLeft,
  FastArrowRight,
  Message,
  Microphone,
  NavArrowDown,
  NavArrowLeft,
  NavArrowRight,
  Pin,
  Plus,
  Refresh,
  Search,
  Sparks,
  Xmark,
} from 'iconoir-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as z from 'zod';
import { Button } from 'components/ui/button';
import { Card } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Textarea } from 'components/ui/textarea';
import { Skeleton } from 'components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { useCreateAgent, useDeleteAgent, useGetAgentById, useGetAgents } from 'features/chat/hooks/use-agents';
import { useGetLlmModels } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import { useQueryClient } from '@tanstack/react-query';
import { useConfig } from 'hooks/use-config';
import { useToast } from 'hooks/use-toast';
import { cn } from 'lib/utils';
import { AgentsTitleIcon } from 'components/blocks/layout/icons/agents-title-icon';
import ConfirmationModal from 'components/blocks/confirmation-modal/confirmation-modal';
import { VisibilityField } from 'components/blocks/visibility-field';
import {
  DEFAULT_RESOURCE_VISIBILITY,
  type ResourceVisibility,
} from 'types/visibility';
import { AgentModelRegionLogo } from './components/agent-model-region-logo';
import { AgentShareModal } from './components/agent-share-modal';
import { resolveLlmDisplayLabel } from './utils/resolve-llm-display-label';
import { AgentCard, getAgentName, type AgentListItem } from './components/agent-card';
import { generateAndSaveAgentAvatar } from './utils/agent-avatar';
import { getApiErrorDetail } from 'utils/api-error';
import { useCommonStore } from 'state/store/common/useCommonStore';

const DEFAULT_TEMPLATE_KEY = 'general_assistant';

type AgentsTab = 'all' | 'mine' | 'featured' | 'platform' | 'hidden';

const AGENTS_PAGE_SIZE = 30;

const agentsPaginationButtonClassName =
  'h-8 w-8 rounded-full border border-grey-100 bg-white p-0 text-grey-900 shadow-none hover:bg-white hover:text-grey-900 disabled:border-grey-100 disabled:bg-white disabled:text-grey-250 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-card dark:disabled:border-border dark:disabled:bg-card dark:disabled:text-muted-foreground';

const agentFormInputClassName =
  '!h-12 w-full self-stretch rounded-lg border border-grey-100 bg-white !px-5 !py-4 text-base font-normal leading-[1.6] shadow-none placeholder:text-grey-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring dark:border-border dark:bg-card';

const createAgentButtonClassName =
  '';

import ConfigurationLayout from 'components/configuration/ConfigurationLayout';
import ConfigurationHeader from 'components/configuration/ConfigurationHeader';
import ConfigurationHeading from 'components/configuration/ConfigurationHeading';
import ConfigurationButton from 'components/configuration/ConfigurationButton';
import { configureCardGridClassName } from 'constant/configure-layout';
import { ChevronDown, FileText, MessageSquare } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import {
  createAgentChatNavigation,
  defaultCreateAgentChatPrompt,
} from 'features/chat/utils/create-agent-chat-navigation';
import { useLanguageContext } from 'i18n/language-context';

type CreateAgentFormData = {
  name: string;
  description: string;
  instructions: string;
  visibility: ResourceVisibility;
};

interface AgentsEmptyStateProps {
  title: string;
  description: string;
  showCreateButton?: boolean;
  onCreateClick?: () => void;
}

const agentsEmptyStateButtonClassName =
  'h-10 shrink-0 rounded-[62px] border border-indigo-100 bg-gradient-to-br from-white to-indigo-25 px-6 text-base font-medium leading-[1.15] text-indigo-950 shadow-none hover:opacity-90 dark:border-border dark:from-card dark:to-card dark:text-foreground dark:hover:opacity-100';

function AgentsEmptyState({ title, description, showCreateButton = true, onCreateClick }: AgentsEmptyStateProps) {
  const { t } = useTranslation();

  return (
    <Card className="flex h-[315px] w-full flex-col items-center justify-center gap-6 rounded-lg border border-grey-100 bg-white p-6 shadow-none dark:border-border dark:bg-card">
      <div className="flex flex-col items-center gap-1.5 text-center">
        <p className="text-sm font-semibold leading-[1.2] text-grey-900 dark:text-foreground">{title}</p>
        <p className="text-xs font-normal leading-[1.2] text-grey-500 dark:text-muted-foreground">{description}</p>
      </div>
      {showCreateButton && onCreateClick ? (
        <Button type="button" variant="outline" className={agentsEmptyStateButtonClassName} onClick={onCreateClick}>
          {t('CREATE_AGENT') || 'Create agent'}
        </Button>
      ) : null}
    </Card>
  );
}

export default function AgentPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<AgentsTab>('all');
  const [page, setPage] = useState(1);
  const userId = useCommonStore((state) => state.user?.id);
  const [selectedAgent, setSelectedAgent] = useState<AgentListItem | null>(null);
  const [isDescribeAgentDialogOpen, setIsDescribeAgentDialogOpen] = useState(false);
  const [selectedModelOption, setSelectedModelOption] = useState('auto');
  const [agentToDelete, setAgentToDelete] = useState<AgentListItem | null>(null);
  const [agentToShare, setAgentToShare] = useState<AgentListItem | null>(null);
  const { t, i18n } = useTranslation();
  const { currentLanguage } = useLanguageContext();
  const { configurationSettings } = useConfig();
  const { toast } = useToast();
  const { mutate: createAgent, isPending: isCreatingAgent } = useCreateAgent();
  const { mutate: deleteAgent, isPending: isDeletingAgent } = useDeleteAgent();
  const createAgentSchema = z.object({
    name: z.string().trim().min(1, t('AGENT_NAME_REQUIRED') || 'Agent name is required'),
    description: z.string().trim().min(1, t('DESCRIPTION_REQUIRED') || 'Description is required'),
    instructions: z.string().optional(),
    visibility: z.enum(['private', 'public']),
  });
  const form = useForm<CreateAgentFormData>({
    resolver: zodResolver(createAgentSchema),
    defaultValues: {
      name: '',
      description: '',
      instructions: '',
      visibility: DEFAULT_RESOURCE_VISIBILITY,
    },
    mode: 'onChange',
  });

  const projectKey = configurationSettings?.blocksKey || '';
  const trimmedSearch = search.trim();
  const agentsQueryFilters = useMemo(
    () => ({
      project_key: projectKey,
      ...(trimmedSearch ? { name: trimmedSearch } : {}),
    }),
    [projectKey, trimmedSearch]
  );

  const tabQueryFilters = useMemo(() => {
    const show_hidden_agent = activeTab === 'hidden';
    if (activeTab === 'mine' && userId) return { created_by: [userId], show_hidden_agent };
    if (activeTab === 'featured') return { created_by: ['catalog'], show_hidden_agent };
    if (activeTab === 'platform') return { platform_managed: true, show_hidden_agent };
    return { show_hidden_agent };
  }, [activeTab, userId]);

  const agentsListEnabled = activeTab !== 'mine' || !!userId;

  const countQueryBase = {
    limit: 1,
    offset: 0,
    show_hidden_agent: false as const,
    ...agentsQueryFilters,
  };

  const { data: myAgentsCountData } = useGetAgents(
    {
      ...countQueryBase,
      created_by: [userId || ''],
    },
    { scope: 'count-mine', enabled: !!userId }
  );

  const { data: featuredAgentsCountData } = useGetAgents(
    {
      ...countQueryBase,
      created_by: ['catalog'],
    },
    { scope: 'count-featured' }
  );

  const { data: platformAgentsCountData } = useGetAgents(
    {
      ...countQueryBase,
      platform_managed: true,
    },
    { scope: 'count-platform' }
  );

  const { data: hiddenAgentsCountData } = useGetAgents(
    {
      ...countQueryBase,
      show_hidden_agent: true,
    },
    { scope: 'count-hidden' }
  );

  const { data: agentsData, isLoading } = useGetAgents(
    {
      limit: AGENTS_PAGE_SIZE,
      offset: (page - 1) * AGENTS_PAGE_SIZE,
      ...agentsQueryFilters,
      ...tabQueryFilters,
    },
    { scope: 'list', enabled: agentsListEnabled }
  );

  const paginatedAgents = useMemo<AgentListItem[]>(() => {
    return (agentsData?.agents as AgentListItem[]) || [];
  }, [agentsData?.agents]);

  const recentFromFirstPage = useRef<AgentListItem[]>([]);
  if (page === 1 && !isLoading) {
    recentFromFirstPage.current = paginatedAgents.slice(0, 4);
  }
  const recentAgents = page === 1 && !isLoading ? paginatedAgents.slice(0, 4) : recentFromFirstPage.current;

  const myAgentsCount = myAgentsCountData?.total_count ?? 0;
  const featuredAgentsCount = featuredAgentsCountData?.total_count ?? 0;
  const platformAgentsCount = platformAgentsCountData?.total_count ?? 0;
  const hiddenAgentsCount = hiddenAgentsCountData?.total_count ?? 0;

  const tabs = useMemo(
    () =>
      [
        { label: t('AGENTS_TAB_ALL') || 'All', value: 'all' as const },
        { label: t('AGENT_TAB_MY') || 'Your Agents', value: 'mine' as const },
        { label: t('AGENTS_TAB_FEATURED') || 'Featured', value: 'featured' as const },
        { label: t('AGENTS_TAB_PLATFORM') || 'Platform', value: 'platform' as const },
        { label: t('AGENTS_TAB_HIDDEN', { defaultValue: 'Hidden' }), value: 'hidden' as const },
      ] satisfies Array<{ label: string; value: AgentsTab }>,
    [t]
  );

  let sectionTitle = t('ALL_AGENTS') || 'All agents';
  if (activeTab === 'mine') sectionTitle = t('MY_AGENTS') || 'Your Agents';
  if (activeTab === 'featured') sectionTitle = t('FEATURED_AGENTS') || 'Featured';
  if (activeTab === 'platform') sectionTitle = t('PLATFORM_AGENTS') || 'Platform';
  if (activeTab === 'hidden') sectionTitle = t('HIDDEN_AGENTS', { defaultValue: 'Hidden' });

  let emptyAgentsDescription =
    t('GET_STARTED_CREATE_FIRST_AGENT', {
      defaultValue: 'Get started by creating your first agent.',
    }) || 'Get started by creating your first agent.';
  if (trimmedSearch) {
    emptyAgentsDescription = t('NO_AGENTS_MATCHED_SEARCH') || 'No agents matched your search.';
  } else if (activeTab === 'mine') {
    emptyAgentsDescription = t('NO_MY_AGENTS') || 'You have not created any agents yet.';
  } else if (activeTab === 'featured') {
    emptyAgentsDescription = t('NO_FEATURED_AGENTS') || 'No featured agents available.';
  } else if (activeTab === 'platform') {
    emptyAgentsDescription = t('NO_PLATFORM_AGENTS') || 'No platform agents available.';
  } else if (activeTab === 'hidden') {
    emptyAgentsDescription = t('NO_HIDDEN_AGENTS', { defaultValue: 'No hidden agents.' });
  }

  const listTotalCount = agentsData?.total_count ?? 0;
  const { data: allCountData } = useGetAgents(countQueryBase, { scope: 'total-count' });
  const totalCount = allCountData?.total_count ?? 0;
  const pageCount = Math.max(1, Math.ceil(listTotalCount / AGENTS_PAGE_SIZE));

  useEffect(() => {
    setPage(1);
  }, [trimmedSearch, activeTab]);

  useEffect(() => {
    if (!isLoading && listTotalCount > 0 && page > pageCount) {
      setPage(pageCount);
    }
  }, [page, pageCount, isLoading, listTotalCount]);

  // Check for create query parameter and open the modal
  useEffect(() => {
    const shouldCreate = searchParams.get('create') === 'true';
    if (shouldCreate) {
      setIsDescribeAgentDialogOpen(true);
      // Remove the query parameter after opening the modal
      searchParams.delete('create');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const isFullyEmpty =
    !isLoading && activeTab === 'all' && listTotalCount === 0 && !trimmedSearch;
  const selectedAgentName = selectedAgent ? getAgentName(selectedAgent, t('UNTITLED_AGENT') || 'Untitled Agent') : '';
  const selectedAgentDescription = selectedAgent?.description || (t('NO_DESCRIPTION') || 'No description');
  const selectedAgentInitial = selectedAgentName.charAt(0).toUpperCase();
  const selectedAgentTag = selectedAgent?.tags?.[0] || selectedAgent?.role || '';

  const { data: selectedAgentData, isLoading: isSelectedAgentDetailsLoading } = useGetAgentById(
    { id: selectedAgent?.id || '' },
    { enabled: !!selectedAgent?.id }
  );
  const { data: llmModels = [] } = useGetLlmModels();

  const selectedAgentModelDisplay = useMemo(() => {
    const llmConfig = selectedAgentData?.agent?.llm_config;

    if (!llmConfig?.model_name) {
      return {
        label: t('SELECT_A_MODEL') || 'Select a model...',
        region: undefined as string | undefined,
      };
    }

    return resolveLlmDisplayLabel({
      currentLlmModel: llmConfig.model_name,
      currentLlmProvider: llmConfig.provider,
      currentLlmModelId: llmConfig.model_id ?? '',
      isBlocksProvider: llmConfig.is_blocks_provider ?? true,
      availableChatModels: llmModels.filter((model) => model.is_system !== false),
      availableCustomChatModels: llmModels.filter((model) => model.is_system === false),
      fallbackLabel: llmConfig.model_name,
    });
  }, [llmModels, selectedAgentData?.agent?.llm_config, t]);

  const agentToDeleteName = agentToDelete ? getAgentName(agentToDelete) : '';

  const handleStartAgentChat = () => {
    if (!selectedAgent) return;

    setSelectedAgent(null);
    navigate(`/chat?agentId=${encodeURIComponent(selectedAgent.id)}`);
  };

  const handleEditAgent = () => {
    if (!selectedAgent) return;

    setSelectedAgent(null);
    navigate(`/agent-details/${encodeURIComponent(selectedAgent.id)}`);
  };

  const handleConfigureAgentCard = (agent: AgentListItem) => {
    navigate(`/agent-details/${encodeURIComponent(agent.id)}/configuration`);
  };

  const handleCreateAgentWithChat = () => {
    const language = currentLanguage || i18n?.language;
    const fallbackPrompt = defaultCreateAgentChatPrompt(language);
    const translatedPrompt = t('CREATE_AGENT_CHAT_DEFAULT_PROMPT', {
      defaultValue: fallbackPrompt,
    });
    const { to, state } = createAgentChatNavigation({
      language,
      translatedPrompt: typeof translatedPrompt === 'string' ? translatedPrompt : undefined,
    });

    navigate(to, { state });
  };

  const resetCreateAgentForm = () => {
    form.reset({
      name: '',
      description: '',
      instructions: '',
      visibility: DEFAULT_RESOURCE_VISIBILITY,
    });
    setSelectedModelOption('auto');
  };

  const handleCloseDescribeAgentDialog = () => {
    if (isCreatingAgent) return;
    setIsDescribeAgentDialogOpen(false);
    resetCreateAgentForm();
  };

  const handleSubmit = (values: CreateAgentFormData) => {
    const blocksKey = configurationSettings?.blocksKey || '';
    if (!blocksKey) {
      toast({
        title: t('UNABLE_TO_CREATE_AGENT') || 'Unable to create agent',
        description:
          t('PROJECT_KEY_MISSING_REFRESH_TRY_AGAIN') || 'Project key is missing. Please refresh and try again.',
        variant: 'destructive',
      });
      return;
    }

    createAgent(
      {
        name: values.name.trim(),
        description: values.description.trim(),
        create_from: DEFAULT_TEMPLATE_KEY,
        project_key: blocksKey,
        is_any_agent: false,
        visibility: values.visibility,
        generateAvatarWithAI: false,
      },
      {
        onSuccess: (response) => {
          const createdAgentId = response?.item_id;

          toast({
            variant: 'success',
            title: t('AGENT_CREATED') || 'Agent created',
            description:
              t('ENTITY_CREATED_SUCCESSFULLY', { name: values.name.trim() }) ||
              `${values.name.trim()} created successfully.`,
          });
          setIsDescribeAgentDialogOpen(false);
          resetCreateAgentForm();

          if (createdAgentId) {
            navigate(`/agent-details/${encodeURIComponent(createdAgentId)}/configuration`);

            // Generate and save DiceBear Bottts avatar in the background
            generateAndSaveAgentAvatar({
              agentId: createdAgentId,
              name: values.name.trim(),
              description: values.description.trim(),
              projectKey: blocksKey,
              configurationSettings,
              agent: response?.agent,
              queryClient,
            }).catch((avatarError) => {
              console.error('Failed to generate agent avatar in background:', avatarError);
            });
          }
        },
        onError: (error) => {
          console.error('Failed to create agent:', error);
          toast({
            title: t('FAILED_TO_CREATE_AGENT') || 'Failed to create agent',
            description: getApiErrorDetail(error) ?? (t('PLEASE_TRY_AGAIN') || 'Please try again.'),
            variant: 'destructive',
          });
        },
      }
    );
  };

  const handleDeleteAgent = () => {
    const blocksKey = configurationSettings?.blocksKey || '';

    if (!agentToDelete?.id || !blocksKey) {
      toast({
        title: t('UNABLE_TO_DELETE_AGENT') || 'Unable to delete agent',
        description:
          t('PROJECT_KEY_MISSING_REFRESH_TRY_AGAIN') || 'Project key is missing. Please refresh and try again.',
        variant: 'destructive',
      });
      return;
    }

    deleteAgent(
      {
        id: agentToDelete.id,
        project_key: blocksKey,
      },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: t('AGENT_DELETED') || 'Agent deleted',
            description:
              t('AGENT_DELETED_SUCCESSFULLY', { name: agentToDeleteName }) ||
              `${agentToDeleteName} has been deleted successfully.`,
          });
          if (selectedAgent?.id === agentToDelete.id) {
            setSelectedAgent(null);
          }
          setAgentToDelete(null);
        },
        onError: (error) => {
          console.error('Failed to delete agent:', error);
          toast({
            title: t('FAILED_TO_DELETE_AGENT') || 'Failed to delete agent',
            description:
              getApiErrorDetail(error) ?? (t('PLEASE_TRY_AGAIN') || 'Please try again.'),
            variant: 'destructive',
          });
        },
      }
    );
  };

  return (
    <ConfigurationLayout>
      <div className="flex w-full min-w-0 flex-col gap-6 border-b border-grey-100 pb-6 dark:border-border">
        <div className="flex w-full min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <ConfigurationHeader
            title={t('AGENTS') || 'Agents'}
            subtitle={
              t('AGENT_PAGE_SUBTITLE', {
                defaultValue:
                  'Specialized chatbots you can configure for specific use cases or documents.',
              }) ||
              t('AGENT_DESCRIPTION') ||
              'Specialized chatbots you can configure for specific use cases or documents.'
            }
            icon={
              <AgentsTitleIcon
                width={20}
                height={20}
                className="text-grey-900 dark:text-foreground"
              />
            }
          />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <ConfigurationButton icon={<Plus className="h-4 w-4" />} data-buddy-icon="create-agent">
                {t('CREATE_AGENT') || 'Create Agent'}
                <ChevronDown className="h-4 w-4 opacity-90" />
              </ConfigurationButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={8}
              className="w-max max-w-[calc(100vw-2rem)] rounded-2xl border border-grey-100 bg-white p-2 shadow-xl dark:border-border dark:bg-card"
            >
              <DropdownMenuItem
                className="flex h-auto cursor-pointer items-start gap-3 rounded-xl p-2.5 transition-colors focus:bg-grey-50 focus:text-foreground dark:focus:bg-white/10"
                onClick={handleCreateAgentWithChat}
              >
                <MessageSquare className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                <div className="space-y-0.5">
                  <div className="text-[15px] font-medium leading-5 text-foreground">
                    {t('CREATE_AGENT_FROM_CHAT', { defaultValue: 'Create agent from chat' }) ||
                      'Create agent from chat'}
                  </div>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="flex h-auto cursor-pointer items-start gap-3 rounded-xl p-2.5 transition-colors focus:bg-grey-50 focus:text-foreground dark:focus:bg-white/10"
                onClick={() => setIsDescribeAgentDialogOpen(true)}
              >
                <FileText className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                <div className="space-y-0.5">
                  <div className="text-[15px] font-medium leading-5 text-foreground">
                    {t('CREATE_AGENT_DESCRIBE') || 'Create agent'}
                  </div>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex h-12 w-full min-w-0 items-center gap-3 rounded-lg border border-grey-100 bg-white p-3 sm:max-w-[460px] dark:border-border dark:bg-card">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center p-[3px]">
            <Search width={15} height={15} strokeWidth={1.75} className="text-grey-400" />
          </div>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('SEARCH_AGENTS_PLACEHOLDER', { defaultValue: 'Search agents...' }) || t('SEARCH_AGENTS') || 'Search agents...'}
            className="h-full min-h-0 flex-1 border-0 bg-transparent p-0 text-sm font-normal leading-[1.2] shadow-none placeholder:text-grey-400 focus-visible:ring-0 dark:bg-transparent"
          />
        </div>
      </div>

      <div className="flex min-w-0 flex-col gap-10 pb-8">
        <div className="inline-flex flex-wrap items-center gap-2">
          {tabs.map((tab) => {
            const tabCounts: Record<AgentsTab, number> = {
              all: totalCount,
              mine: myAgentsCount,
              featured: featuredAgentsCount,
              platform: platformAgentsCount,
              hidden: hiddenAgentsCount,
            };
            const count = tabCounts[tab.value];
            const isActive = activeTab === tab.value;

            return (
              <button
                key={tab.value}
                type="button"
                onClick={() => setActiveTab(tab.value)}
                className={cn(
                  'inline-flex h-10 items-center rounded-lg border px-3 text-sm font-normal leading-[1.2] transition-colors',
                  isActive
                    ? 'border-grey-100 bg-grey-50 text-grey-900 dark:border-true-white/40 dark:bg-true-white/15 dark:text-true-white'
                    : 'border-transparent text-grey-500 hover:bg-grey-50 hover:text-grey-900 dark:text-true-white/75 dark:hover:bg-true-white/10 dark:hover:text-true-white'
                )}
              >
                {tab.label}
                <span
                  className={cn(
                    'ml-1.5 text-xs',
                    isActive
                      ? 'text-grey-500 dark:text-true-white/80'
                      : 'text-grey-500 dark:text-true-white/55'
                  )}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {!isFullyEmpty ? (
          <div className="flex min-w-0 flex-col gap-3 border-b border-grey-100 pb-8 dark:border-border">
            <div className="flex items-center gap-2">
              <Clock width={20} height={20} strokeWidth={1.75} className="text-grey-900 dark:text-foreground" />
              <ConfigurationHeading title={t('RECENTLY_USED') || 'Recently Used'} />
            </div>

            {isLoading && page === 1 ? (
              <Card className="rounded-lg border border-grey-50 bg-white shadow-none dark:border-border dark:bg-card">
                <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
                  <Refresh width={16} height={16} strokeWidth={1.75} className="animate-spin" />
                  {t('LOADING_AGENTS') || 'Loading agents...'}
                </div>
              </Card>
            ) : recentAgents.length > 0 ? (
              <div className={configureCardGridClassName}>
                {recentAgents.map((agent) => (
                  <AgentCard
                    key={`recent-${agent.id}`}
                    agent={agent}
                    projectKey={projectKey}
                    onSelect={setSelectedAgent}
                    onConfigureClick={handleConfigureAgentCard}
                    onShareClick={setAgentToShare}
                    onDeleteClick={setAgentToDelete}
                    isHiddenList={activeTab === 'hidden'}
                  />
                ))}
              </div>
            ) : (
              <Card className="rounded-lg border border-dashed border-grey-100 bg-white shadow-none dark:border-border dark:bg-card">
                <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
                  <CubeBandage width={16} height={16} strokeWidth={1.75} />
                  {t('NO_AGENTS_AVAILABLE') || 'No agents available'}
                </div>
              </Card>
            )}
          </div>
        ) : null}

        <div className="flex min-w-0 flex-col gap-3">
          <div className="flex min-w-0 items-center justify-between gap-3 sm:gap-4">
            <ConfigurationHeading title={sectionTitle} />
            {/* <button
              type="button"
              className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-grey-50 p-3 text-sm font-normal leading-[1.2] text-grey-900 dark:bg-white/10 dark:text-foreground"
            >
              {t('MOST_POPULAR') || 'Most popular'}
              <NavArrowDown width={16} height={16} strokeWidth={1.75} />
            </button> */}
          </div>

          {isLoading ? (
            <Card className="rounded-lg border border-grey-50 bg-white shadow-none dark:border-border dark:bg-card">
              <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
                <Refresh width={16} height={16} strokeWidth={1.75} className="animate-spin" />
                {t('LOADING_AGENTS') || 'Loading agents...'}
              </div>
            </Card>
          ) : paginatedAgents.length > 0 ? (
            <>
              <div className={configureCardGridClassName}>
                {paginatedAgents.map((agent) => (
                  <AgentCard
                    key={agent.id}
                    agent={agent}
                    projectKey={projectKey}
                    onSelect={setSelectedAgent}
                    onConfigureClick={handleConfigureAgentCard}
                    onShareClick={setAgentToShare}
                    onDeleteClick={setAgentToDelete}
                    isHiddenList={activeTab === 'hidden'}
                  />
                ))}
              </div>

              {listTotalCount > 0 ? (
                <div className="mt-6 flex w-full items-center justify-end gap-4">
                  <p className="text-sm font-normal leading-[1.2] text-grey-900 dark:text-foreground">
                    {t('PAGE_OF', {
                      page,
                      total: pageCount,
                      defaultValue: `Page ${page} of ${pageCount}`,
                    })}
                  </p>

                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className={agentsPaginationButtonClassName}
                      onClick={() => setPage(1)}
                      disabled={page <= 1}
                      aria-label={t('GO_TO_FIRST_PAGE') || 'Go to first page'}
                    >
                      <FastArrowLeft width={16} height={16} strokeWidth={1.75} />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className={agentsPaginationButtonClassName}
                      onClick={() => setPage((current) => Math.max(1, current - 1))}
                      disabled={page <= 1}
                      aria-label={t('GO_TO_PREVIOUS_PAGE') || 'Go to previous page'}
                    >
                      <NavArrowLeft width={16} height={16} strokeWidth={1.75} />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className={agentsPaginationButtonClassName}
                      onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
                      disabled={page >= pageCount}
                      aria-label={t('GO_TO_NEXT_PAGE') || 'Go to next page'}
                    >
                      <NavArrowRight width={16} height={16} strokeWidth={1.75} />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className={agentsPaginationButtonClassName}
                      onClick={() => setPage(pageCount)}
                      disabled={page >= pageCount}
                      aria-label={t('GO_TO_LAST_PAGE') || 'Go to last page'}
                    >
                      <FastArrowRight width={16} height={16} strokeWidth={1.75} />
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <AgentsEmptyState
              title={t('NO_AGENTS_FOUND') || 'No agents found'}
              description={emptyAgentsDescription}
              showCreateButton={
                !trimmedSearch &&
                activeTab !== 'featured' &&
                activeTab !== 'platform' &&
                activeTab !== 'hidden'
              }
              onCreateClick={() => setIsDescribeAgentDialogOpen(true)}
            />
          )}
        </div>
      </div>

      <ConfirmationModal
        open={!!agentToDelete}
        onOpenChange={(open) => {
          if (!open) {
            setAgentToDelete(null);
          }
        }}
        title={t('DELETE_AGENT') || 'Delete agent'}
        description={
          agentToDeleteName
            ? t('DELETE_AGENT_CONFIRMATION_WITH_NAME', { name: agentToDeleteName }) ||
            `Are you sure you want to delete ${agentToDeleteName}? This action cannot be undone.`
            : t('DELETE_AGENT_CONFIRMATION') || 'Are you sure you want to delete this agent? This action cannot be undone.'
        }
        onConfirm={handleDeleteAgent}
        confirmText={t('DELETE') || 'Delete'}
        cancelText={t('CANCEL') || 'Cancel'}
        preventAutoClose
        isLoading={isDeletingAgent}
        confirmVariant="delete"
      />

      {agentToShare?.id ? (
        <AgentShareModal
          agentId={agentToShare.id}
          agentName={getAgentName(agentToShare)}
          open={!!agentToShare}
          onOpenChange={(open) => {
            if (!open) setAgentToShare(null);
          }}
        />
      ) : null}

      <Dialog
        open={isDescribeAgentDialogOpen}
        onOpenChange={(open) => {
          if (!open && isCreatingAgent) return;
          setIsDescribeAgentDialogOpen(open);
          if (!open) {
            resetCreateAgentForm();
          }
        }}
      >
        <DialogContent
          className="w-[calc(100vw-2rem)] max-w-[640px] gap-0 overflow-hidden rounded-xl p-0 shadow-2xl"
          showOverlay
          hideClose
        >
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="relative flex max-h-[88vh] flex-col p-8">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-4 top-4 h-6 w-6 text-grey-700 hover:bg-transparent hover:text-grey-700 focus-visible:ring-0 dark:text-grey-500 dark:hover:bg-transparent dark:hover:text-grey-500"
                onClick={handleCloseDescribeAgentDialog}
                disabled={isCreatingAgent}
              >
                <Xmark width={20} height={20} strokeWidth={1.75} />
              </Button>

              <div className="flex min-h-0 flex-1 flex-col gap-10 overflow-y-auto">
                <div className="flex w-full flex-col gap-2 text-grey-700 dark:text-muted-foreground">
                  <h2 className="text-2xl font-semibold leading-[1.2] text-grey-900 dark:text-foreground">
                    {t('NEW_AGENT') || 'New Agent'}
                  </h2>
                  <p className="text-base font-normal leading-[1.6]">
                    {t('CREATE_YOUR_OWN_UNIQUE_SETUP') || 'Create your own unique setup'}
                  </p>
                </div>

                <div className="flex w-full flex-col gap-6 pb-4">
                  <div className="flex w-full flex-col gap-6">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem className="gap-1 space-y-0">
                          <FormLabel className="text-sm font-medium leading-[1.6] text-grey-700 dark:text-muted-foreground">
                            {t('AGENT_NAME') || 'Agent Name'}
                            <span className="text-error">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t('AGENT_NAME_PLACEHOLDER') || 'e.g. Customer Support Agent'}
                              {...field}
                              className={agentFormInputClassName}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem className="gap-1 space-y-0">
                          <FormLabel className="text-sm font-medium leading-[1.6] text-grey-700 dark:text-muted-foreground">
                            {t('DESCRIPTION') || 'Description'}
                            <span className="text-error-500">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={
                                t('BRIEFLY_DESCRIBE_AI_AGENT') ||
                                'Briefly describe the AI agent you want to create'
                              }
                              {...field}
                              className={agentFormInputClassName}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="visibility"
                      render={({ field }) => (
                        <FormItem className="gap-1 space-y-0">
                          <FormControl>
                            <VisibilityField
                              value={field.value}
                              onValueChange={field.onChange}
                              idPrefix="create-agent-visibility"
                              required
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* <div className="flex w-full flex-col gap-1 border-b border-grey-100 pb-6 dark:border-border">
                      <FormLabel className="text-sm font-semibold leading-[1.2] text-grey-900 dark:text-foreground">
                        {t('INSTRUCTIONS') || 'Instructions'}
                      </FormLabel>
                      <FormField
                        control={form.control}
                        name="instructions"
                        render={({ field }) => (
                          <FormItem className="space-y-0">
                            <FormControl>
                              <div className="overflow-hidden rounded-xl border border-grey-100 bg-white dark:border-border dark:bg-card">
                                <Textarea
                                  placeholder={
                                    t('AGENT_INSTRUCTIONS_PLACEHOLDER') ||
                                    'What does this agent do? How should it behave? What should it avoid doing?'
                                  }
                                  rows={6}
                                  {...field}
                                  className="min-h-[188px] resize-none rounded-none border-0 px-4 pb-6 pt-4 text-base leading-[1.6] shadow-none placeholder:text-grey-300 focus-visible:ring-0"
                                />
                                <div className="flex items-center justify-between border-t border-grey-50 px-4 py-1.5 dark:border-border">
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      className="inline-flex items-center gap-1.5 rounded border border-grey-100 bg-grey-50 px-2 py-1 text-sm font-medium text-grey-500 dark:border-border dark:bg-white/10 dark:text-muted-foreground"
                                    >
                                      <Sparks width={16} height={16} strokeWidth={1.75} />
                                      {t('OPTIMIZE') || 'Optimize'}
                                    </button>
                                    <button
                                      type="button"
                                      className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-white-50 text-grey-700 dark:border-border dark:text-foreground"
                                    >
                                      <Microphone width={20} height={20} strokeWidth={1.75} />
                                    </button>
                                  </div>
                                  <div className="flex h-6 items-center rounded-sm border border-grey-100 bg-grey-50 px-1.5 py-0.5 dark:border-border dark:bg-white/10">
                                    <div className="h-3 w-3 rounded-sm border border-grey-100 dark:border-border" />
                                  </div>
                                </div>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="flex w-full flex-col gap-4 border-b border-grey-100 pb-6 dark:border-border">
                      <div className="flex items-start gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold leading-[1.2] text-grey-900 dark:text-foreground">
                            {t('MODEL') || 'Model'}
                          </p>
                          <p className="mt-1 text-xs font-medium leading-[1.6] text-grey-500 dark:text-muted-foreground">
                            {t('SELECT_MODEL_FOR_AGENT') || 'Select the model that should power your agent.'}
                          </p>
                        </div>
                        <Select value={selectedModelOption} onValueChange={setSelectedModelOption}>
                          <SelectTrigger className="h-auto w-[160px] rounded-lg border-0 bg-grey-50 px-3 py-3 text-sm font-normal text-grey-900 shadow-none focus:ring-0 dark:bg-white/10 dark:text-foreground [&_svg]:opacity-70">
                            <SelectValue placeholder={t('AUTO') || 'Auto'} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="auto">{t('AUTO') || 'Auto'}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div> */}
                </div>
              </div>

              <div className="flex w-full items-center justify-end gap-4 border-t border-grey-100 pt-6 dark:border-border">
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 rounded-[62px] border-indigo-100 bg-gradient-to-br from-white to-indigo-25 px-6 text-base font-medium leading-[115%] text-indigo-950 shadow-none hover:opacity-90 dark:border-border dark:from-card dark:to-card dark:text-foreground"
                  onClick={handleCloseDescribeAgentDialog}
                  disabled={isCreatingAgent}
                >
                  {t('CANCEL') || 'Cancel'}
                </Button>
                <Button
                  type="submit"
                  className={`h-11 rounded-[62px] border-0 bg-black px-8 text-base font-semibold leading-[115%] shadow-none hover:bg-grey-900 ${createAgentButtonClassName}`}
                  disabled={isCreatingAgent || !form.formState.isValid}
                >
                  {isCreatingAgent ? t('CREATING') || 'Creating...' : t('CREATE_AGENT') || 'Create Agent'}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedAgent} onOpenChange={(open) => !open && setSelectedAgent(null)}>
        <DialogContent
          className="w-[calc(100vw-2rem)] max-w-[640px] gap-0 overflow-hidden rounded-xl p-0 shadow-2xl"
          showOverlay
          hideClose
        >
          <div className="relative flex max-h-[88vh] flex-col items-center gap-10 overflow-y-auto p-8">
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-4 h-6 w-6 text-grey-700 hover:bg-transparent hover:text-grey-700 focus-visible:ring-0 dark:text-grey-500 dark:hover:bg-transparent dark:hover:text-grey-500"
              onClick={() => setSelectedAgent(null)}
            >
              <Xmark width={20} height={20} strokeWidth={1.75} />
            </Button>

            <div className="flex w-full flex-col items-center gap-10">
              <div className="flex w-full flex-col items-center gap-6">
                <div className="flex w-full flex-col items-center gap-6">
                  <div className="flex w-full flex-col items-center gap-4">
                    <Avatar className="relative flex aspect-square h-[60px] w-[60px] shrink-0 flex-col items-center justify-center gap-2.5 overflow-hidden rounded-[100px] border-0 bg-grey-50 dark:bg-white/10">
                      {selectedAgent?.logo_url ? (
                        <AvatarImage
                          src={selectedAgent.logo_url}
                          alt={selectedAgentName}
                          className="aspect-square h-full w-full rounded-[100px] object-cover"
                        />
                      ) : null}
                      <AvatarFallback className="flex h-full w-full flex-col items-center justify-center gap-2.5 rounded-[100px] bg-grey-50 text-[30px] font-semibold leading-[1.2] text-grey-700 dark:bg-white/10 dark:text-grey-500">
                        {selectedAgentInitial}
                      </AvatarFallback>
                    </Avatar>

                    <DialogHeader className="flex w-full flex-col items-center gap-4 self-stretch break-words pr-0 text-center not-italic">
                      <DialogTitle className="max-w-full shrink-0 break-words text-center text-2xl font-semibold leading-[1.2] text-grey-900 dark:text-foreground">
                        {selectedAgentName}
                      </DialogTitle>
                      <DialogDescription asChild>
                        <p className="w-full max-w-[540px] shrink-0 break-words text-center text-base font-normal leading-[1.6] text-grey-700 dark:text-muted-foreground">
                          {selectedAgentDescription}
                        </p>
                      </DialogDescription>
                    </DialogHeader>
                  </div>

                  {selectedAgentTag ? (
                    <span className="rounded-md bg-grey-50 px-4 py-1.5 text-sm font-semibold leading-[1.2] text-grey-700 dark:bg-white/10 dark:text-grey-500">
                      {selectedAgentTag}
                    </span>
                  ) : null}
                </div>

                <div className="flex flex-col items-center">
                  <div className="flex min-w-[320px] max-w-full items-center justify-center gap-1.5 rounded-lg border border-grey-100 bg-white px-8 py-4 dark:border-border dark:bg-card">
                    <Cube
                      width={16}
                      height={16}
                      strokeWidth={1.75}
                      className="shrink-0 text-grey-900 dark:text-foreground"
                    />
                    <div className="flex min-w-0 items-center gap-1.5 text-sm leading-[1.2] text-grey-900 dark:text-foreground">
                      <span className="shrink-0 font-normal">{t('MODEL') || 'Model'}:</span>
                      {isSelectedAgentDetailsLoading ? (
                        <div className="flex min-w-0 flex-1 items-center gap-1.5">
                          <Skeleton className="h-4 w-40 max-w-full rounded-md" />
                          <Skeleton className="h-4 w-4 shrink-0 rounded-full" />
                        </div>
                      ) : (
                        <div className="flex min-w-0 items-center gap-1.5">
                          <span className="break-words font-semibold">{selectedAgentModelDisplay.label}</span>
                          <AgentModelRegionLogo region={selectedAgentModelDisplay.region} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex w-full items-center justify-center border-t border-grey-100 pt-6 dark:border-border">
              <div className="flex flex-wrap items-center justify-center gap-4">
                <Button
                  variant="outline"
                  className="h-11 gap-1.5 rounded-[62px] border-indigo-100 bg-gradient-to-br from-white to-indigo-25 px-6 text-base font-medium leading-[115%] text-indigo-950 shadow-none hover:opacity-90 dark:border-border dark:from-card dark:to-card dark:text-foreground"
                  onClick={handleEditAgent}
                  disabled={!selectedAgent}
                >
                  <Edit width={16} height={16} strokeWidth={1.75} />
                  {t('EDIT_AGENT') || 'Edit Agent'}
                </Button>
                <Button
                  className={`h-11 gap-1.5 rounded-[62px] border-0 px-8 text-base font-semibold leading-[115%] shadow-none [&_svg]:text-inherit ${createAgentButtonClassName}`}
                  onClick={handleStartAgentChat}
                  disabled={!selectedAgent}
                >
                  <ChatBubbleEmpty width={16} height={16} strokeWidth={1.75} />
                  {t('START_CHAT') || 'Start Chat'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </ConfigurationLayout>
  );
}